import type { SupervisionReport } from '@epc/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, Printer } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date as fmtDate, todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Checkbox, Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

/**
 * Протокол совещания и отчёт для заказчика (УЧ-04): открытые на дату замечания и вопросы к проекту, решения
 * за период и открытые поручения по надзору; печать и Excel.
 */

const minusDays = (d: string, n: number) =>
  new Date(new Date(`${d}T00:00:00Z`).getTime() - n * 86_400_000).toISOString().slice(0, 10);

export function ReportPanel({ projectId }: { projectId?: string }) {
  const [date, setDate] = useState(todayLocal());
  const [from, setFrom] = useState(minusDays(todayLocal(), 13));
  const [internal, setInternal] = useState(false);
  const query = { projectId: projectId ?? '', date, from, internal: internal ? '1' : '0' };
  const r = useQuery({
    queryKey: ['supervision-report', query],
    queryFn: () => api<SupervisionReport>('/supervision/report', { query }),
    enabled: Boolean(projectId) && date >= from,
    placeholderData: keepPreviousData,
    retry: false,
  });
  if (!projectId)
    return (
      <EmptyState title="Выберите проект">
        Отчёт собирается по проекту: открытые замечания и вопросы на дату, решения за период и поручения.
      </EmptyState>
    );
  const qs = new URLSearchParams(query).toString();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 print:hidden md:flex-row md:flex-wrap md:items-end">
        <Field label="На дату">
          <Input
            type="date"
            className="h-11 md:h-10"
            value={date}
            max={todayLocal()}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Решения с">
          <Input
            type="date"
            className="h-11 md:h-10"
            value={from}
            max={date}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Checkbox
          className="min-h-11 items-center md:min-h-10"
          checked={internal}
          onChange={(e) => setInternal(e.target.checked)}
          label="Со внутренними замечаниями ОТК, ОТ и ПБ"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            className="h-11 md:h-10"
            icon={<Printer className="size-4" />}
            onClick={() => window.print()}
          >
            Печать
          </Button>
          <a
            href={`/api/v1/supervision/report.xlsx?${qs}`}
            className="inline-flex h-11 items-center gap-2 rounded-md border border-line px-4 text-[14px] font-medium hover:bg-surface-2 md:h-10"
          >
            <Download className="size-4" /> Скачать Excel
          </a>
        </div>
      </div>
      {r.isPending ? <Spinner /> : null}
      <ErrorBox error={r.error} />
      {r.data ? <ReportBody r={r.data} /> : null}
    </div>
  );
}

function ReportBody({ r }: { r: SupervisionReport }) {
  return (
    <div className="flex flex-col gap-4 text-[14px]">
      <div>
        <h2 className="text-[18px] font-semibold">
          Отчёт по надзору {r.projectCode} на {fmtDate(r.date)}
        </h2>
        <p className="text-ink-2 [overflow-wrap:anywhere]">
          {r.projectName}. Открыто замечаний: {r.remarks.length}, из них просрочено{' '}
          {r.remarks.filter((x) => x.overdue).length}; вопросов к проекту: {r.questions.length}; решений с{' '}
          {fmtDate(r.from)}: {r.decisions.length}.
        </p>
      </div>
      <Panel title={`Открытые замечания (${r.remarks.length})`}>
        {r.remarks.length ? (
          <ul className="flex flex-col divide-y divide-line">
            {r.remarks.map((x) => (
              <li key={x.id} className="flex flex-col gap-0.5 py-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Link
                    to="/supervision/remarks/$remarkId"
                    params={{ remarkId: x.id }}
                    className="inline-flex min-h-11 items-center font-medium underline-offset-4 hover:underline md:min-h-0"
                  >
                    {x.number}
                  </Link>
                  <span className="[overflow-wrap:anywhere]">{x.title}</span>
                  {x.stopWork ? <Badge tone="danger">стоп работ</Badge> : null}
                  {x.overdue ? <Badge tone="warning">просрочено</Badge> : null}
                </div>
                <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {[x.source, x.status, `срок ${fmtDate(x.dueDate)}`, x.contractor, x.assignee, x.where]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-2">Открытых замечаний на эту дату нет.</p>
        )}
      </Panel>
      <Panel title={`Вопросы к проекту (${r.questions.length})`}>
        {r.questions.length ? (
          <ul className="flex flex-col divide-y divide-line">
            {r.questions.map((x) => (
              <li key={x.id} className="flex flex-col gap-0.5 py-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Link
                    to="/supervision/questions/$questionId"
                    params={{ questionId: x.id }}
                    className="inline-flex min-h-11 items-center font-medium underline-offset-4 hover:underline md:min-h-0"
                  >
                    {x.number}
                  </Link>
                  <span className="[overflow-wrap:anywhere]">{x.subject}</span>
                  {x.critical ? <Badge tone="danger">работы стоят</Badge> : null}
                  {x.overdue ? <Badge tone="warning">ответ просрочен</Badge> : null}
                </div>
                <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {[x.status, x.dueDate ? `ответ до ${fmtDate(x.dueDate)}` : null, x.author, x.where]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-2">Открытых вопросов на эту дату нет.</p>
        )}
      </Panel>
      <Panel title={`Решения за период (${r.decisions.length})`}>
        {r.decisions.length ? (
          <ul className="flex flex-col divide-y divide-line">
            {r.decisions.map((x) => (
              <li key={`${x.kind}-${x.id}`} className="flex flex-col gap-0.5 py-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {x.kind === 'question' ? (
                    <Link
                      to="/supervision/questions/$questionId"
                      params={{ questionId: x.id }}
                      className="inline-flex min-h-11 items-center font-medium underline-offset-4 hover:underline md:min-h-0"
                    >
                      {x.number}
                    </Link>
                  ) : (
                    <Link
                      to="/supervision/remarks/$remarkId"
                      params={{ remarkId: x.id }}
                      className="inline-flex min-h-11 items-center font-medium underline-offset-4 hover:underline md:min-h-0"
                    >
                      {x.number}
                    </Link>
                  )}
                  <span className="[overflow-wrap:anywhere]">{x.title}</span>
                  <span className="text-[12px] text-ink-3">{fmtDate(x.at)}</span>
                </div>
                <span className="whitespace-pre-wrap text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {x.decision}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-2">За период решений не было.</p>
        )}
      </Panel>
      <Panel title={`Поручения по надзору (${r.tasks.length})`}>
        {r.tasks.length ? (
          <ul className="flex flex-col divide-y divide-line">
            {r.tasks.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-1.5 py-2">
                {x.link ? (
                  <a
                    href={x.link}
                    className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0 [overflow-wrap:anywhere]"
                  >
                    {x.title}
                  </a>
                ) : (
                  <span className="[overflow-wrap:anywhere]">{x.title}</span>
                )}
                <span className="text-[13px] text-ink-2">
                  {[x.assignee, x.dueDate ? `срок ${fmtDate(x.dueDate)}` : null].filter(Boolean).join(' · ')}
                </span>
                {x.overdue ? <Badge tone="warning">просрочено</Badge> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-2">Открытых поручений нет.</p>
        )}
      </Panel>
    </div>
  );
}
